import {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Button,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from "react-icons/fa6";
import { FilteredOfficeHandbook } from "@/app/components/handbooks/filteredOfficeHandbook";
import { api } from "@/api";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";

export interface AgreementOfficeItem {
  officeId: number | null;
  workplaceId: number | null;
  price: number;
}

export interface AgreementOfficesTableRef {
  getOfficesData: () => {
    offices: Array<{ officeId: number; price: number }>;
    workplaces: Array<{ workplaceId: number; price: number }>;
  } | null;
}

interface AgreementOfficesTableProps {
  agreementId: number;
  initialOffices?: Array<{ officeId: number; price: number }>;
  initialWorkplaces?: Array<{ workplaceId: number; price: number }>;
  showSaveButton?: boolean;
}

export const AgreementOfficesTable = forwardRef<
  AgreementOfficesTableRef,
  AgreementOfficesTableProps
>(
  (
    {
      agreementId,
      initialOffices = [],
      initialWorkplaces = [],
      showSaveButton = false,
    },
    ref,
  ) => {
    const [offices, setOffices] = useState<AgreementOfficeItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const handleError = useErrorHandling();

    // Загружаем и объединяем офисы и рабочие места в единый список
    useEffect(() => {
      const loadOffices = async () => {
        try {
          setIsLoading(true);

          // Объединяем offices и workplaces в единый список
          const combinedOffices: AgreementOfficeItem[] = [];

          // Добавляем офисы
          initialOffices.forEach((office) => {
            combinedOffices.push({
              officeId: office.officeId,
              workplaceId: null,
              price: office.price,
            });
          });

          // Добавляем рабочие места
          // Для рабочих мест нужно получить officeId из handbook
          try {
            const workplacesResponse = await api.offices.workplaces.list();
            initialWorkplaces.forEach((workplace) => {
              const workplaceData = workplacesResponse.data.find(
                (w) => w.id === workplace.workplaceId,
              );
              combinedOffices.push({
                officeId: workplaceData?.office_id || null,
                workplaceId: workplace.workplaceId,
                price: workplace.price,
              });
            });
          } catch (workplaceErr) {
            // Если не удалось загрузить рабочие места, добавляем без officeId
            initialWorkplaces.forEach((workplace) => {
              combinedOffices.push({
                officeId: null,
                workplaceId: workplace.workplaceId,
                price: workplace.price,
              });
            });
          }

          // Если нет данных, добавляем пустую строку
          if (combinedOffices.length === 0) {
            combinedOffices.push({
              officeId: null,
              workplaceId: null,
              price: 0,
            });
          }

          setOffices(combinedOffices);
        } catch (err: any) {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        } finally {
          setIsLoading(false);
        }
      };

      if (agreementId) {
        loadOffices();
      }
    }, [agreementId, initialOffices, initialWorkplaces, handleError]);

    const handleAddOffice = useCallback(() => {
      const newOffices = [
        ...offices,
        { officeId: null, workplaceId: null, price: 0 },
      ];
      setOffices(newOffices);
    }, [offices]);

    const handleRemoveOffice = useCallback(
      (index: number) => {
        if (offices.length <= 1) {
          return; // Нельзя удалить последний офис
        }
        const newOffices = offices.filter((_, i) => i !== index);
        setOffices(newOffices);
      },
      [offices],
    );

    const handleMoveUp = useCallback(
      (index: number) => {
        if (index === 0) return;
        const newOffices = [...offices];
        [newOffices[index - 1], newOffices[index]] = [
          newOffices[index],
          newOffices[index - 1],
        ];
        setOffices(newOffices);
      },
      [offices],
    );

    const handleMoveDown = useCallback(
      (index: number) => {
        if (index === offices.length - 1) return;
        const newOffices = [...offices];
        [newOffices[index], newOffices[index + 1]] = [
          newOffices[index + 1],
          newOffices[index],
        ];
        setOffices(newOffices);
      },
      [offices],
    );

    const handleOfficeChange = useCallback(
      (index: number, officeId: string | number | null) => {
        const newOffices = [...offices];
        newOffices[index] = {
          ...newOffices[index],
          officeId: officeId ? Number(officeId) : null,
          workplaceId: null, // Сбрасываем рабочее место при смене офиса
        };
        setOffices(newOffices);
      },
      [offices],
    );

    const handlePriceChange = useCallback(
      (index: number, price: number) => {
        const newOffices = [...offices];
        newOffices[index] = {
          ...newOffices[index],
          price: price,
        };
        setOffices(newOffices);
      },
      [offices],
    );

    // Функция для получения списка ID офисов, которые нужно исключить для конкретной строки
    const getExcludedOfficeIds = useCallback(
      (currentIndex: number): number[] => {
        return offices
          .map((item, idx) => (idx !== currentIndex ? item.officeId : null))
          .filter((id): id is number => id !== null && id !== 0);
      },
      [offices],
    );

    // Метод для получения данных офисов (используется через ref)
    const getOfficesData = useCallback((): {
      offices: Array<{ officeId: number; price: number }>;
      workplaces: Array<{ workplaceId: number; price: number }>;
    } | null => {
      // Валидация: проверяем, что хотя бы один офис выбран
      const hasValidOffice = offices.some(
        (office) => office.officeId !== null && office.officeId !== 0,
      );

      if (!hasValidOffice) {
        return null;
      }

      // Разделяем офисы и рабочие места
      const officesData = offices
        .filter((item) => item.officeId !== null && !item.workplaceId)
        .map((item) => ({
          officeId: item.officeId!,
          price: item.price,
        }));

      const workplacesData = offices
        .filter((item) => item.workplaceId !== null)
        .map((item) => ({
          workplaceId: item.workplaceId!,
          price: item.price,
        }));

      return {
        offices: officesData,
        workplaces: workplacesData,
      };
    }, [offices]);

    // Экспортируем метод через ref
    useImperativeHandle(ref, () => ({
      getOfficesData,
    }));

    const handleSave = useCallback(async () => {
      try {
        // Валидация: проверяем, что хотя бы один офис выбран
        const hasValidOffice = offices.some(
          (office) => office.officeId !== null && office.officeId !== 0,
        );

        if (!hasValidOffice) {
          toast.error("Необходимо выбрать хотя бы один офис");
          return;
        }

        // Разделяем офисы и рабочие места
        const officesData = offices
          .filter((item) => item.officeId !== null && !item.workplaceId)
          .map((item) => ({
            officeId: item.officeId!,
            price: item.price,
          }));

        // Обновляем договор через API
        const agreementResponse = await api.agreements.get(agreementId);
        const agreement = agreementResponse;

        await api.agreements.update(agreementId, {
          ...agreement,
          offices: officesData,
        });

        toast.success("Офисы договора успешно сохранены");
      } catch (err: any) {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      }
    }, [offices, agreementId, handleError]);

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Загрузка...</div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <span className="text-lg font-semibold">Офисы</span>
          <div className="flex gap-2">
            <Button
              startContent={<FaPlus />}
              color="primary"
              variant="bordered"
              onPress={handleAddOffice}>
              Добавить офис
            </Button>
            {showSaveButton && (
              <Button color="primary" variant="solid" onPress={handleSave}>
                Сохранить
              </Button>
            )}
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table aria-label="Офисы" removeWrapper>
            <TableHeader>
              <TableColumn>Офис</TableColumn>
              <TableColumn>Цена</TableColumn>
              <TableColumn width={120}>Действия</TableColumn>
            </TableHeader>
            <TableBody>
              {offices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-gray-500">
                    Нет офисов. Нажмите "Добавить офис" для добавления.
                  </TableCell>
                </TableRow>
              ) : (
                offices.map((office, index) => (
                  <TableRow
                    key={`office-${index}-${office.officeId || "empty"}`}>
                    <TableCell>
                      <FilteredOfficeHandbook
                        key={`handbook-${index}-${office.officeId || "empty"}-${getExcludedOfficeIds(index).join(",")}`}
                        isRequired
                        variant="bordered"
                        selectedKey={
                          office.officeId ? String(office.officeId) : ""
                        }
                        onSelectionChange={(key) =>
                          handleOfficeChange(index, key)
                        }
                        excludeOfficeIds={getExcludedOfficeIds(index)}
                        children={<></>}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        variant="bordered"
                        placeholder="Цена"
                        value={office.price.toString()}
                        onValueChange={(value) =>
                          handlePriceChange(index, Number(value) || 0)
                        }
                        startContent={
                          <span className="text-default-400">₽</span>
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-row gap-1">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={() => handleMoveUp(index)}
                          isDisabled={index === 0}>
                          <FaArrowUp />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={() => handleMoveDown(index)}
                          isDisabled={index === offices.length - 1}>
                          <FaArrowDown />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          color="danger"
                          onPress={() => handleRemoveOffice(index)}
                          isDisabled={offices.length <= 1}>
                          <FaTrash />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  },
);

AgreementOfficesTable.displayName = "AgreementOfficesTable";
